/**
 * useMCPState Hook
 *
 * Aggregates application state for the MCP bridge.
 * Exposes ONLY control-plane data (IDs, statuses, counts).
 * NEVER exposes PII or sensitive data.
 */

import { useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth';

export function useMCPState() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { getToken, isAuthenticated } = useAuth();
  const notificationsRef = useRef({
    lastAction: null,
    lastError: null,
    history: [],
  });

  // Get current page name from path
  const getPageName = useCallback(() => {
    const path = location.pathname;
    if (path === '/') return 'dashboard';
    const segment = path.split('/')[1];
    return segment || 'dashboard';
  }, [location.pathname]);

  // Get route params
  const getRouteParams = useCallback(() => {
    const parts = location.pathname.split('/').filter(Boolean);
    const params = {};
    if (parts.length > 1) {
      params.id = parts[1];
    }
    return params;
  }, [location.pathname]);

  // Get query metadata (status, counts, IDs - never actual data)
  const getQueryMetadata = useCallback(() => {
    const cache = queryClient.getQueryCache();
    const queries = {};

    cache.getAll().forEach((query) => {
      const key = query.queryKey.join('-');
      const state = query.state;

      const meta = {
        status: state.status,
        isStale: query.isStale(),
        updatedAt: state.dataUpdatedAt,
      };

      // For list queries, expose total count and IDs only
      if (state.data && Array.isArray(state.data)) {
        meta.total = state.data.length;
        meta.ids = state.data
          .slice(0, 100)
          .map((item) => item?.id)
          .filter(Boolean);
      } else if (state.data?.items && Array.isArray(state.data.items)) {
        meta.total = state.data.total || state.data.items.length;
        meta.ids = state.data.items
          .slice(0, 100)
          .map((item) => item?.id)
          .filter(Boolean);
      } else if (state.data?.id) {
        // Single entity - expose ID only
        meta.entityId = state.data.id;
      }

      queries[key] = meta;
    });

    return queries;
  }, [queryClient]);

  // Record action result
  const recordAction = useCallback((action, result, error = null) => {
    const entry = {
      action,
      success: !error,
      timestamp: Date.now(),
      error: error?.message || null,
    };

    if (error) {
      notificationsRef.current.lastError = entry;
    } else {
      notificationsRef.current.lastAction = entry;
    }

    notificationsRef.current.history = [
      entry,
      ...notificationsRef.current.history.slice(0, 9),
    ];
  }, []);

  // Create waitFor helpers with timeout support
  const createWaitFor = useCallback(() => ({
    /**
     * Wait for app to be ready (authenticated)
     * @param {number} timeout - Max wait time in ms (default: 30000)
     * @returns {Promise<{success: true, elapsed: number}>}
     * @throws {{success: false, error: string, elapsed: number}} on timeout
     */
    ready: (timeout = 30000) =>
      new Promise((resolve, reject) => {
        const start = Date.now();
        const check = () => {
          const elapsed = Date.now() - start;
          if (window.__GETCLEARANCE__?.ready) {
            resolve({ success: true, elapsed });
          } else if (elapsed >= timeout) {
            reject({ success: false, error: 'Timeout waiting for ready', elapsed });
          } else {
            setTimeout(check, 100);
          }
        };
        check();
      }),

    /**
     * Wait for a specific query to succeed
     * @param {string} queryKey - The query key to wait for
     * @param {number} timeout - Max wait time in ms (default: 30000)
     * @returns {Promise<{success: true, elapsed: number, meta: object}>}
     */
    querySuccess: (queryKey, timeout = 30000) =>
      new Promise((resolve, reject) => {
        const start = Date.now();
        const check = () => {
          const elapsed = Date.now() - start;
          const meta = window.__GETCLEARANCE__?.queries?.[queryKey];
          if (meta?.status === 'success') {
            resolve({ success: true, elapsed, meta });
          } else if (elapsed >= timeout) {
            reject({ success: false, error: `Timeout waiting for query: ${queryKey}`, elapsed });
          } else {
            setTimeout(check, 200);
          }
        };
        check();
      }),

    /**
     * Wait for page to finish loading
     * @param {number} timeout - Max wait time in ms (default: 30000)
     * @returns {Promise<{success: true, elapsed: number}>}
     */
    noLoading: (timeout = 30000) =>
      new Promise((resolve, reject) => {
        const start = Date.now();
        const check = () => {
          const elapsed = Date.now() - start;
          const el = document.querySelector('[data-page-loading="true"]');
          if (!el) {
            resolve({ success: true, elapsed });
          } else if (elapsed >= timeout) {
            reject({ success: false, error: 'Timeout waiting for loading to complete', elapsed });
          } else {
            setTimeout(check, 200);
          }
        };
        check();
      }),

    /**
     * Wait for a DOM element to appear
     * @param {string} selector - CSS selector to wait for
     * @param {number} timeout - Max wait time in ms (default: 30000)
     * @returns {Promise<{success: true, elapsed: number, element: Element}>}
     */
    element: (selector, timeout = 30000) =>
      new Promise((resolve, reject) => {
        const start = Date.now();
        const check = () => {
          const elapsed = Date.now() - start;
          const el = document.querySelector(selector);
          if (el) {
            resolve({ success: true, elapsed, element: el });
          } else if (elapsed >= timeout) {
            reject({ success: false, error: `Timeout waiting for element: ${selector}`, elapsed });
          } else {
            setTimeout(check, 100);
          }
        };
        check();
      }),

    /**
     * Wait for a condition to be true
     * @param {function} condition - Function that returns truthy value when condition is met
     * @param {number} timeout - Max wait time in ms (default: 30000)
     * @returns {Promise<{success: true, elapsed: number, value: any}>}
     */
    condition: (condition, timeout = 30000) =>
      new Promise((resolve, reject) => {
        const start = Date.now();
        const check = () => {
          const elapsed = Date.now() - start;
          try {
            const value = condition();
            if (value) {
              resolve({ success: true, elapsed, value });
            } else if (elapsed >= timeout) {
              reject({ success: false, error: 'Timeout waiting for condition', elapsed });
            } else {
              setTimeout(check, 100);
            }
          } catch (err) {
            if (elapsed >= timeout) {
              reject({ success: false, error: `Timeout with error: ${err.message}`, elapsed });
            } else {
              setTimeout(check, 100);
            }
          }
        };
        check();
      }),

    /**
     * Wait for route to change to expected path
     * @param {string} path - Expected path (can be partial match)
     * @param {number} timeout - Max wait time in ms (default: 30000)
     * @returns {Promise<{success: true, elapsed: number, path: string}>}
     */
    route: (path, timeout = 30000) =>
      new Promise((resolve, reject) => {
        const start = Date.now();
        const check = () => {
          const elapsed = Date.now() - start;
          const currentPath = window.__GETCLEARANCE__?.route?.path || window.location.pathname;
          if (currentPath.includes(path)) {
            resolve({ success: true, elapsed, path: currentPath });
          } else if (elapsed >= timeout) {
            reject({ success: false, error: `Timeout waiting for route: ${path}`, elapsed, actual: currentPath });
          } else {
            setTimeout(check, 100);
          }
        };
        check();
      }),
  }), []);

  // Create assertion helpers
  const createAssertions = useCallback(() => ({
    /**
     * Assert current page matches expected name
     * @param {string} expected - Expected page name
     * @returns {{passed: boolean, actual: string, expected: string, message: string}}
     */
    pageIs: (expected) => {
      const actual = window.__GETCLEARANCE__?.route?.page || '';
      const passed = actual === expected;
      return {
        passed,
        actual,
        expected,
        message: passed ? `Page is ${expected}` : `Expected page ${expected}, got ${actual}`,
      };
    },

    /**
     * Assert query has data with items
     * @param {string} queryKey - Query key to check
     * @returns {{passed: boolean, actual: object, message: string}}
     */
    hasData: (queryKey) => {
      const meta = window.__GETCLEARANCE__?.queries?.[queryKey];
      const passed = meta?.status === 'success' && (meta?.total > 0 || meta?.entityId);
      return {
        passed,
        actual: meta || null,
        message: passed ? `Query ${queryKey} has data` : `Query ${queryKey} has no data or failed`,
      };
    },

    /**
     * Assert no errors are present
     * @returns {{passed: boolean, lastError: object|null, message: string}}
     */
    noErrors: () => {
      const lastError = window.__GETCLEARANCE__?.notifications?.lastError;
      const toastErrors = document.querySelectorAll('[data-testid*="error"]');
      const passed = !lastError && toastErrors.length === 0;
      return {
        passed,
        lastError: lastError || null,
        toastErrorCount: toastErrors.length,
        message: passed ? 'No errors detected' : `Error detected: ${lastError?.error || 'toast error'}`,
      };
    },

    /**
     * Assert DOM element exists
     * @param {string} selector - CSS selector
     * @returns {{passed: boolean, selector: string, message: string}}
     */
    elementExists: (selector) => {
      const el = document.querySelector(selector);
      const passed = !!el;
      return {
        passed,
        selector,
        message: passed ? `Element ${selector} exists` : `Element ${selector} not found`,
      };
    },

    /**
     * Assert form is valid
     * @param {string} formId - Form data-testid
     * @returns {{passed: boolean, formId: string, message: string}}
     */
    formValid: (formId) => {
      const form = document.querySelector(`[data-testid="${formId}"]`);
      if (!form) {
        return { passed: false, formId, message: `Form ${formId} not found` };
      }
      const isValid = form.getAttribute('data-form-valid') === 'true';
      return {
        passed: isValid,
        formId,
        message: isValid ? `Form ${formId} is valid` : `Form ${formId} is invalid`,
      };
    },

    /**
     * Assert multiple conditions at once
     * @param {function[]} assertions - Array of assertion functions
     * @returns {{passed: boolean, results: object[], failedCount: number}}
     */
    all: (...assertions) => {
      const results = assertions.map(fn => fn());
      const failedCount = results.filter(r => !r.passed).length;
      return {
        passed: failedCount === 0,
        results,
        failedCount,
        message: failedCount === 0 ? 'All assertions passed' : `${failedCount} assertion(s) failed`,
      };
    },
  }), []);

  return {
    route: {
      path: location.pathname,
      page: getPageName(),
      params: getRouteParams(),
      search: location.search,
    },
    ready: isAuthenticated,
    navigate,
    queryClient,
    getToken,
    getQueryMetadata,
    notifications: notificationsRef.current,
    recordAction,
    createWaitFor,
    createAssertions,
  };
}
